import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown, MapPin, Phone, ExternalLink } from 'lucide-react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { 
    name: 'About Us', 
    href: '/about',
    dropdownItems: [
      { name: 'Company History', href: '/about#company-history' },
      { name: 'Mission & Vision', href: '/about#mission-vision' },
      { name: 'Our Team', href: '/about#our-team' },
      { name: 'Join Us', href: '/about#join-us' }
    ]
  },
  {
    name: 'Services',
    href: '#',
    dropdownItems: [
      { name: 'Product Expertise', href: '/work' },
      { name: 'Customer Service', href: '/custom-services' }
    ]
  },
  { 
    name: 'Projects', 
    href: '/project',
    dropdownItems: [
      { name: 'Featured Projects', href: '/project' },
      { name: 'More Projects', href: '/more-projects' }
    ]
  },
  { name: 'Careers', href: '/careers' },
  { name: 'Contact', href: '/contact' },
  {
    name: 'Other Companies',
    href: '#',
    dropdownItems: [
      { name: 'HOP-SHING', href: 'https://hop-shing.com', external: true },
      { name: 'HOP SHING ENGINEERING & CONSTRUCTION', href: '#', external: true, comingSoon: true },
      { name: 'HOP SHING(CHINA)', href: '#', external: true, comingSoon: true },
      { name: 'CONCRETE SOLUTION', href: '#', external: true, comingSoon: true },
      { name: 'SPECIALTY PRODUCT', href: '#', external: true, comingSoon: true }
    ]
  }
];

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileDropdown, setMobileDropdown] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', message: '' });
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      setScrolled(offset > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isOpen || showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, showModal]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = element.offsetTop - 100;
      window.scrollTo({
        top: offset,
        behavior: 'smooth'
      });
    }
  };

  const handleNavigation = (href: string, comingSoon?: boolean, external?: boolean) => {
    setIsOpen(false);
    setMobileDropdown(null);
    
    if (comingSoon) {
      setModalContent({
        title: 'Coming Soon',
        message: 'This website will be coming soon!'
      });
      setShowModal(true);
      return;
    }

    if (external) {
      window.location.href = href;
      return;
    }
    
    if (href.includes('#')) {
      const [path, hash] = href.split('#');
      const currentPath = location.pathname;
      
      if (currentPath === path) {
        scrollToSection(hash);
      } else {
        navigate(path);
        setTimeout(() => {
          scrollToSection(hash);
        }, 100);
      }
    } else {
      navigate(href);
      window.scrollTo(0, 0);
    }
  };

  return (
    <>
      {/* Info Section */}
      <div className="bg-[#070738] text-white py-2">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-end md:space-x-8">
            <div className="flex items-center space-x-2">
              <Phone size={24} className="text-primary-light" />
              <span className="text-sm">+852 2570 3759</span>
            </div>
            <div className="flex items-center mt-2 md:mt-0">
              <div className="flex items-center space-x-2">
                <MapPin size={24} className="text-primary-light" />
                <span className="text-sm">3/F, Ocean Industrial Building, 29 Tai Yip Street, Kwun Tong, Hong Kong</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header 
        className="sticky top-0 left-0 right-0 w-full bg-white shadow-md z-50"
      >
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-3 md:py-4">
            <RouterLink 
              to="/" 
              className="flex items-center transition-transform duration-300 transform hover:scale-110 relative z-50"
              onClick={() => window.scrollTo(0, 0)}
            >
              <img 
                src="/assets/images/logo.png"
                alt="Hop Shing Logo"
                className="h-16 md:h-20 lg:h-24 w-auto object-contain"
                style={{
                  WebkitUserDrag: 'none',
                  userDrag: 'none',
                  WebkitTouchCallout: 'none'
                }}
              />
            </RouterLink>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6 lg:space-x-8">
              {navItems.map((item) => (
                <div 
                  key={item.name} 
                  className="relative group"
                  onMouseEnter={() => setActiveDropdown(item.name)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  {item.dropdownItems ? (
                    <>
                      <button
                        className="text-base lg:text-lg font-medium transition-all duration-300 group-hover:text-primary-light flex items-center gap-2 text-gray-800"
                        aria-expanded={activeDropdown === item.name}
                        aria-haspopup="true"
                      >
                        {item.name}
                        <ChevronDown 
                          size={16} 
                          className={`transition-transform duration-300 ${
                            activeDropdown === item.name ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      <div 
                        className={`absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-xl py-2 transition-all duration-300 ${
                          activeDropdown === item.name 
                            ? 'opacity-100 visible translate-y-0' 
                            : 'opacity-0 invisible -translate-y-2'
                        }`}
                      >
                        {item.dropdownItems.map((dropdownItem) => (
                          <button
                            key={dropdownItem.name}
                            className="flex items-center justify-between w-full text-left px-4 py-2 text-gray-800 hover:bg-primary-light hover:text-white transition-all duration-300"
                            onClick={() => {
                              setActiveDropdown(null);
                              handleNavigation(dropdownItem.href, dropdownItem.comingSoon, dropdownItem.external);
                            }}
                          >
                            <span className="text-sm">{dropdownItem.name}</span>
                            {dropdownItem.external && (
                              <ExternalLink size={16} className="ml-2 flex-shrink-0" />
                            )}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <button
                      className="text-base lg:text-lg font-medium transition-all duration-300 hover:text-primary-light text-gray-800"
                      onClick={() => handleNavigation(item.href)}
                    >
                      {item.name}
                    </button>
                  )}
                </div>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 rounded-lg transition-colors duration-300 text-gray-800 hover:bg-gray-100 relative z-50"
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? "Close menu" : "Open menu"}
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <div 
          className={`fixed inset-0 bg-black/50 md:hidden transition-opacity duration-300 ${
            isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
          style={{ zIndex: 999 }}
          onClick={() => setIsOpen(false)}
        >
          {/* Mobile Menu Panel */}
          <div 
            className={`fixed right-0 top-0 h-full w-[280px] bg-white shadow-xl transition-transform duration-300 ease-in-out ${
              isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            style={{ zIndex: 1000 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex flex-col h-full">
              <div className="flex justify-end p-4 border-b border-gray-100">
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded-lg text-gray-600 hover:text-primary-light transition-colors duration-300"
                >
                  <X size={24} />
                </button>
              </div>

              <nav className="flex-1 overflow-y-auto px-6 py-8">
                {navItems.map((item) => (
                  <div key={item.name} className="mb-6">
                    {item.dropdownItems ? (
                      <div>
                        <button
                          onClick={() => setMobileDropdown(mobileDropdown === item.name ? null : item.name)}
                          className="flex items-center justify-between w-full text-lg font-medium text-gray-800 mb-2"
                        >
                          <span>{item.name}</span>
                          <ChevronDown 
                            size={20} 
                            className={`transition-transform duration-300 ${
                              mobileDropdown === item.name ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        <div
                          className={`pl-4 space-y-2 overflow-hidden transition-all duration-300 ${
                            mobileDropdown === item.name ? 'max-h-[300px] opacity-100' : 'max-h-0 opacity-0'
                          }`}
                        >
                          {item.dropdownItems.map((dropdownItem) => (
                            <button
                              key={dropdownItem.name}
                              className="flex items-center justify-between w-full text-left py-2 text-gray-600 hover:text-primary-light transition-colors text-sm"
                              onClick={() => handleNavigation(dropdownItem.href, dropdownItem.comingSoon, dropdownItem.external)}
                            >
                              <span>{dropdownItem.name}</span>
                              {dropdownItem.external && (
                                <ExternalLink size={16} className="ml-2 flex-shrink-0" />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <button
                        className="block w-full text-left text-lg font-medium text-gray-800 hover:text-primary-light transition-colors"
                        onClick={() => handleNavigation(item.href)}
                      >
                        {item.name}
                      </button>
                    )}
                  </div>
                ))}
              </nav>
            </div>
          </div>
        </div>
      </header>

      {/* Modal */}
      {showModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1001] p-4"
          onClick={() => setShowModal(false)}
        >
          <div 
            className="bg-white rounded-lg p-6 max-w-md w-full transform transition-all duration-300 scale-100"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-gray-800 mb-4">{modalContent.title}</h3>
            <p className="text-gray-600 mb-6">
              {modalContent.message}
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-primary-light text-white rounded-md hover:bg-primary/80 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;